import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockLeads } from '@/mocks/leads';
import { mockPartners } from '@/mocks/partners';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import { mockOffers } from '@/mocks/offers';
import { useLoading } from '@/hooks/useLoading';
import { SkeletonRow } from '@/components/Skeleton';
import BentoCard from '@/components/BentoCard';
import {
  Search,
  Filter,
  Download,
  Phone,
  Mail,
  ArrowUpRight,
  MapPin,
  Calendar,
  Tag,
  Route,
  AlertTriangle,
} from 'lucide-react';
import Checkbox from '@/components/base/Checkbox';
import { formatDistanceToNow } from 'date-fns';

function AiIndicator({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--accent)' }} />
      {children}
    </span>
  );
}

export default function LeadsPage() {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    verticals: [] as string[],
    geos: [] as string[],
    statuses: [] as string[],
    routingStatuses: [] as string[],
    partners: [] as string[],
    qualityMin: '',
    dateFrom: '',
    dateTo: '',
  });
  const [expandedLead, setExpandedLead] = useState<string | null>(null);

  const filteredLeads = useMemo(() => {
    let result = mockLeads.filter(l => !l.deleted_at);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        l =>
          (l.firstname || '').toLowerCase().includes(q) ||
          (l.lastname || '').toLowerCase().includes(q) ||
          (l.email || '').toLowerCase().includes(q) ||
          (l.phone || '').toLowerCase().includes(q) ||
          (l.postcode || '').toLowerCase().includes(q) ||
          (l.city || '').toLowerCase().includes(q)
      );
    }

    if (filters.verticals.length > 0) {
      result = result.filter(l => filters.verticals.includes(l.vertical_id));
    }
    if (filters.geos.length > 0) {
      result = result.filter(l => filters.geos.includes(l.geo_id));
    }
    if (filters.statuses.length > 0) {
      result = result.filter(l => filters.statuses.includes(l.status));
    }
    if (filters.routingStatuses.length > 0) {
      result = result.filter(l => filters.routingStatuses.includes(l.routing_status));
    }
    if (filters.partners.length > 0) {
      result = result.filter(l => filters.partners.includes(l.assigned_partner_id || ''));
    }
    if (filters.qualityMin) {
      const min = parseFloat(filters.qualityMin);
      result = result.filter(l => (l.quality_score || 0) >= min);
    }
    if (filters.dateFrom) {
      result = result.filter(l => new Date(l.created_at) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      result = result.filter(l => new Date(l.created_at) <= new Date(filters.dateTo + 'T23:59:59'));
    }

    return result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [searchQuery, filters]);

  const toggleRow = (id: string) => {
    const next = new Set(selectedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedRows(next);
  };

  const toggleAll = () => {
    if (selectedRows.size === filteredLeads.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredLeads.map(l => l.id)));
    }
  };

  const exportCSV = () => {
    const rows = filteredLeads.filter(l => selectedRows.has(l.id));
    if (rows.length === 0) return;
    const headers = ['ID', 'Name', 'Email', 'Phone', 'Vertical', 'Geo', 'Status', 'Routing', 'Quality', 'Partner', 'Created'];
    const csv = [
      headers.join(','),
      ...rows.map(r =>
        [
          r.id,
          `${r.firstname || ''} ${r.lastname || ''}`.trim(),
          r.email || '',
          r.phone || '',
          mockVerticals.find(v => v.id === r.vertical_id)?.name || r.vertical_id,
          mockGeos.find(g => g.id === r.geo_id)?.name || r.geo_id,
          r.status,
          r.routing_status,
          r.quality_score || '',
          mockPartners.find(p => p.id === r.assigned_partner_id)?.display_name || '',
          r.created_at,
        ]
          .map(v => `"${String(v).replace(/"/g, '""')}"`)
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const qualityLabel = (score: number | null) => {
    if (!score) return '—';
    if (score >= 0.7) return 'High';
    if (score >= 0.4) return 'Medium';
    return 'Low';
  };

  const routingPill = (status: string) => {
    switch (status) {
      case 'assigned': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'unmatched': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      case 'duplicate': return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
      case 'pending': return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
      case 'invalid': return { bg: 'var(--bg-surface-raised)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      default: return { bg: 'var(--bg-surface-raised)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const { loading } = useLoading(filteredLeads);

  const toggleLead = (id: string) => {
    setExpandedLead(prev => prev === id ? null : id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Lead Inbox</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{filteredLeads.length} leads</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedRows.size > 0 && (
            <>
              <button
                onClick={exportCSV}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-white rounded-xl transition-all whitespace-nowrap"
                style={{ background: 'var(--accent)' }}
              >
                <Download size={14} />
                Export {selectedRows.size}
              </button>
              <button className="px-3 py-2 text-sm rounded-xl transition-all whitespace-nowrap border"
                style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
              >
                Mark invalid
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search + Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 group">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search name, email, phone, postcode..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl outline-none transition-all duration-300"
            style={{
              background: 'var(--bg-input)',
              border: '1px solid var(--border-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm rounded-xl transition-all whitespace-nowrap border"
          style={{
            background: showFilters ? 'var(--accent)' : 'var(--bg-input)',
            color: showFilters ? 'var(--text-inverse)' : 'var(--text-tertiary)',
            borderColor: 'var(--border-primary)',
          }}
        >
          <Filter size={14} />
          Filters
          {(filters.verticals.length + filters.geos.length + filters.statuses.length + filters.routingStatuses.length + filters.partners.length) > 0 && (
            <span className="text-[10px] font-bold px-1.5 rounded-full" style={{ background: 'var(--danger-dot)', color: 'var(--text-inverse)' }}>
              {filters.verticals.length + filters.geos.length + filters.statuses.length + filters.routingStatuses.length + filters.partners.length}
            </span>
          )}
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="rounded-xl p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 animate-fade-in border"
          style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
        >
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Vertical</label>
            <div className="space-y-1.5">
              {mockVerticals.map(v => (
                <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer transition-colors" style={{ color: 'var(--text-secondary)' }}>
                  <Checkbox
                    checked={filters.verticals.includes(v.id)}
                    onChange={val => {
                      setFilters(prev => ({
                        ...prev,
                        verticals: val ? [...prev.verticals, v.id] : prev.verticals.filter(x => x !== v.id),
                      }));
                    }}
                  />
                  {v.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Routing Status</label>
            <div className="space-y-1.5">
              {['pending', 'assigned', 'unmatched', 'duplicate', 'invalid'].map(s => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer transition-colors" style={{ color: 'var(--text-secondary)' }}>
                  <Checkbox
                    checked={filters.routingStatuses.includes(s)}
                    onChange={val => {
                      setFilters(prev => ({
                        ...prev,
                        routingStatuses: val
                          ? [...prev.routingStatuses, s]
                          : prev.routingStatuses.filter(x => x !== s),
                      }));
                    }}
                  />
                  <span className="capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Partner</label>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {mockPartners.map(p => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer transition-colors" style={{ color: 'var(--text-secondary)' }}>
                  <Checkbox
                    checked={filters.partners.includes(p.id)}
                    onChange={val => {
                      setFilters(prev => ({
                        ...prev,
                        partners: val ? [...prev.partners, p.id] : prev.partners.filter(x => x !== p.id),
                      }));
                    }}
                  />
                  {p.display_name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Quality Score Min</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={filters.qualityMin}
              onChange={e => setFilters(prev => ({ ...prev, qualityMin: e.target.value }))}
              placeholder="0.0 - 1.0"
              className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
            />
            <label className="text-[11px] font-medium uppercase tracking-wider mt-4 mb-2 block" style={{ color: 'var(--text-muted)' }}>Date Range</label>
            <div className="flex gap-2">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={e => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="flex-1 px-2 py-1.5 text-xs rounded-lg outline-none transition-all"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
              />
              <input
                type="date"
                value={filters.dateTo}
                onChange={e => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                className="flex-1 px-2 py-1.5 text-xs rounded-lg outline-none transition-all"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Card Feed */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-xl p-5 border" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
              <SkeletonRow />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLeads.length > 0 && (
            <div className="flex items-center gap-3 px-2">
              <Checkbox
                checked={selectedRows.size === filteredLeads.length && filteredLeads.length > 0}
                onChange={toggleAll}
                indeterminate={selectedRows.size > 0 && selectedRows.size < filteredLeads.length}
              />
              <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                {selectedRows.size > 0 ? `${selectedRows.size} selected` : `${filteredLeads.length} leads`}
              </span>
            </div>
          )}

          {filteredLeads.map(lead => {
            const vertical = mockVerticals.find(v => v.id === lead.vertical_id);
            const geo = mockGeos.find(g => g.id === lead.geo_id);
            const partner = mockPartners.find(p => p.id === lead.assigned_partner_id);
            const offer = mockOffers.find(o => o.id === lead.assigned_offer_id);
            const isExpanded = expandedLead === lead.id;

            const detailPanel = (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Vertical', value: vertical?.name || lead.vertical_id },
                    { label: 'Geo', value: geo?.name || lead.geo_id },
                    { label: 'Status', value: lead.status },
                    { label: 'Routing', value: lead.routing_status },
                  ].map(item => (
                    <div key={item.label} className="rounded-lg p-2.5 border" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-separator)' }}>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                      <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors"
                      style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                    >
                      <Phone size={12} />
                      {lead.phone}
                    </a>
                  )}
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors"
                      style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                    >
                      <Mail size={12} />
                      {lead.email}
                    </a>
                  )}
                  <span className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                  >
                    <MapPin size={12} />
                    {lead.city}, {lead.postcode}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                  >
                    <Calendar size={12} />
                    {formatDistanceToNow(new Date(lead.created_at), { addSuffix: false })} ago
                  </span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                  >
                    <ArrowUpRight size={12} />
                    View Lead
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
                  >
                    <Route size={12} />
                    Re-route
                  </button>
                  {lead.fraud_signals.length > 0 && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border"
                      style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                    >
                      <AlertTriangle size={12} />
                      {lead.fraud_signals.length} fraud signal{lead.fraud_signals.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            );

            return (
              <BentoCard
                key={lead.id}
                size="full"
                expandable
                expanded={isExpanded}
                onToggle={() => toggleLead(lead.id)}
                active={isExpanded}
                className="row-hover"
                header={
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="pt-0.5" onClick={e => { e.stopPropagation(); }}>
                      <Checkbox
                        checked={selectedRows.has(lead.id)}
                        onChange={() => toggleRow(lead.id)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {lead.firstname} {lead.lastname}
                        </h3>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{geo?.name || lead.geo_id}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                          style={routingPill(lead.routing_status)}
                        >{lead.routing_status}</span>
                        {lead.fraud_signals.length > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                            style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                          >
                            Fraud alert
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {lead.phone && (
                          <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                            <Phone size={10} />
                            {lead.phone}
                          </span>
                        )}
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{vertical?.name || lead.vertical_id}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{partner?.display_name || 'Unmatched'}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{formatDistanceToNow(new Date(lead.created_at), { addSuffix: false })} ago</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <AiIndicator>
                        <span className="text-xs font-semibold"
                          style={{
                            color: (lead.quality_score || 0) >= 0.7 ? 'var(--text-primary)' :
                              (lead.quality_score || 0) >= 0.4 ? 'var(--warning-text)' : 'var(--danger-text)',
                          }}
                        >
                          {qualityLabel(lead.quality_score)}
                        </span>
                      </AiIndicator>
                    </div>
                  </div>
                }
                footer={detailPanel}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-md border"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
                  >
                    {vertical?.name || lead.vertical_id}
                  </span>
                  {partner ? (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{partner.display_name}</span>
                  ) : (
                    <span className="text-xs font-medium" style={{ color: 'var(--danger-text)' }}>Unmatched</span>
                  )}
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{offer?.payout_model?.toUpperCase() || '—'}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatDistanceToNow(new Date(lead.created_at), { addSuffix: false })} ago</span>
                  {lead.city && (
                    <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <MapPin size={10} />
                      {lead.city}, {lead.postcode}
                    </span>
                  )}
                </div>
              </BentoCard>
            );
          })}

          {filteredLeads.length === 0 && (
            <div className="text-center py-16 animate-fade-in">
              <p className="text-lg font-semibold" style={{ color: 'var(--text-tertiary)' }}>No leads found</p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Try adjusting your filters or search query.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}